// Single source of truth for the Voice Broadcast realtime cards.
//
// The two sides speak different dialects: /voicebroadcast_realtime/list returns
// `count_data` as a single object keyed `new_count` / `ringing_count` / `answer_count`,
// while each listing row carries a display string in `row.type` ('New' / 'Ringing' /
// 'Answer'). Clicking a card filters the listing, so the pairing lives here once instead
// of being restated in the card and the list components.
//
// `rowType: null` marks the card that clears the filter rather than applying one.

export const TOTAL_STATUS_KEY = 'total';

// Order here is the order the cards render in, and matches what the page showed before.
// Every card carries BOTH `color` (the solid fill: icon chip, bottom border) and `tint` (the
// ~20% card wash) — see the same note in realtimeStatus.js. The wash cannot be built by
// concatenating onto `color` any more: `var(--cx-accent)33` is invalid CSS and is dropped
// silently. Only Total is themed; the other three are status vocabulary and stay fixed hex.
export const BROADCAST_STATUS_CARDS = [
  { key: 'new_count', title: 'New', color: '#2cabe3', tint: '#2cabe333', icon: 'Slash', rowType: 'New' },
  {
    key: 'ringing_count',
    title: 'Ringing',
    color: '#6376ff',
    tint: '#6376ff33',
    icon: 'Rss',
    rowType: 'Ringing'
  },
  {
    key: 'answer_count',
    title: 'Answer',
    color: '#008000',
    tint: '#00800033',
    icon: 'PhoneForwarded',
    rowType: 'Answer'
  },
  {
    key: TOTAL_STATUS_KEY,
    title: 'Total',
    color: 'var(--cx-accent)',
    tint: 'rgba(var(--cx-accent-rgb), 0.2)',
    icon: 'Phone',
    rowType: null
  }
];

export const getBroadcastCard = (key) => BROADCAST_STATUS_CARDS.find((card) => card.key === key);

export const filterRowsByStatus = (rows = [], statusKey) => {
  const card = statusKey ? getBroadcastCard(statusKey) : null;
  if (!card?.rowType) return rows;
  return rows.filter((row) => row.type === card.rowType);
};

// `count_data` arrives as a one-element array holding the counts object.
export const buildBroadcastCounts = (data = []) => {
  const source = data?.[0] || {};

  const counts = BROADCAST_STATUS_CARDS.reduce((acc, card) => {
    if (card.rowType) {
      acc[card.key] = parseInt(source[card.key], 10) || 0;
    }
    return acc;
  }, {});

  counts[TOTAL_STATUS_KEY] = Object.values(counts).reduce((sum, count) => sum + count, 0);
  return counts;
};
